import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { ITEM_PREFIXES } from '../../constants';
export function useNewKeyboardNavigation({ mainRef, btnRef, focusFlattenItems, keyboardNavigationRef, hasListInFocusChain, firstItemId, }) {
    const defaultActiveItemId = hasListInFocusChain ? undefined : firstItemId;
    const [activeItemId, setActiveItemId] = useState(() => defaultActiveItemId);
    const activeItemIdRef = useRef(defaultActiveItemId);
    const resetActiveItemId = useCallback(() => {
        setActiveItemId(defaultActiveItemId);
        activeItemIdRef.current = defaultActiveItemId;
    }, [defaultActiveItemId]);
    const handleListKeyDownFactory = useCallback((ids, expandedIds) => (e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        switch (e.key) {
            case 'ArrowDown': {
                if (activeItemIdRef.current !== undefined) {
                    const activeIndex = ids.findIndex(id => id === activeItemIdRef.current);
                    const nextId = Math.min(activeIndex + 1, ids.length - 1);
                    const itemId = ids[nextId];
                    const item = focusFlattenItems[itemId];
                    activeItemIdRef.current = itemId;
                    setActiveItemId(itemId);
                    if (item.type !== 'group') {
                        (_b = (_a = item.itemRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                }
                else {
                    const itemId = ids[0];
                    const item = focusFlattenItems[itemId];
                    activeItemIdRef.current = itemId;
                    setActiveItemId(itemId);
                    if (item.type !== 'group') {
                        (_d = (_c = item.itemRef) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
                    }
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            case 'ArrowUp': {
                if (ids[0] === activeItemIdRef.current) {
                    if (hasListInFocusChain) {
                        const item = focusFlattenItems[ids[0]];
                        if (item.parentId === ITEM_PREFIXES.default) {
                            activeItemIdRef.current = undefined;
                            setActiveItemId(undefined);
                            (_e = mainRef === null || mainRef === void 0 ? void 0 : mainRef.current) === null || _e === void 0 ? void 0 : _e.focus();
                        }
                    }
                }
                else if (activeItemIdRef.current !== undefined) {
                    const activeIndex = ids.findIndex(id => id === activeItemIdRef.current);
                    const nextId = Math.max(activeIndex - 1, 0);
                    const itemId = ids[nextId];
                    const item = focusFlattenItems[itemId];
                    activeItemIdRef.current = itemId;
                    setActiveItemId(itemId);
                    if (item.type !== 'group') {
                        (_g = (_f = item.itemRef) === null || _f === void 0 ? void 0 : _f.current) === null || _g === void 0 ? void 0 : _g.focus();
                    }
                }
                e.stopPropagation();
                e.preventDefault();
                return;
            }
            case 'ArrowRight': {
                if (activeItemIdRef.current !== undefined && expandedIds.includes(activeItemIdRef.current)) {
                    const item = focusFlattenItems[activeItemIdRef.current];
                    const newItemId = item.items[0];
                    const newItem = focusFlattenItems[newItemId];
                    activeItemIdRef.current = newItemId;
                    setActiveItemId(newItemId);
                    setTimeout(() => { var _a, _b; return (_b = (_a = newItem.itemRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus(); }, 0);
                    e.stopPropagation();
                    e.preventDefault();
                }
                return;
            }
            case 'Tab': {
                if (activeItemIdRef.current !== undefined) {
                    if (hasListInFocusChain) {
                        e.preventDefault();
                        e.stopPropagation();
                        activeItemIdRef.current = undefined;
                        setActiveItemId(undefined);
                        (_h = mainRef === null || mainRef === void 0 ? void 0 : mainRef.current) === null || _h === void 0 ? void 0 : _h.focus();
                    }
                    else {
                        resetActiveItemId();
                    }
                }
                else {
                    btnRef && !e.shiftKey ? (_j = btnRef === null || btnRef === void 0 ? void 0 : btnRef.current) === null || _j === void 0 ? void 0 : _j.focus() : (_k = mainRef === null || mainRef === void 0 ? void 0 : mainRef.current) === null || _k === void 0 ? void 0 : _k.focus();
                }
                return;
            }
            default: {
                return;
            }
        }
    }, [focusFlattenItems, hasListInFocusChain, mainRef, resetActiveItemId, btnRef]);
    const forceUpdateActiveItemId = useCallback((itemId) => {
        var _a, _b;
        setActiveItemId(itemId);
        activeItemIdRef.current = itemId;
        const item = focusFlattenItems[itemId];
        (_b = (_a = item === null || item === void 0 ? void 0 : item.itemRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
    }, [focusFlattenItems]);
    useImperativeHandle(keyboardNavigationRef, () => ({ focusItem: forceUpdateActiveItemId }), [forceUpdateActiveItemId]);
    return {
        resetActiveItemId,
        activeItemId,
        forceUpdateActiveItemId,
        handleListKeyDownFactory,
    };
}
